<template>
  <v-container id="document_signature_customization_root" fluid>
    <!-- header -->
    <v-row class="mb-6 px-6" justify="center" align="center">
      <v-btn
        id="back_button"
        class="elevation-0 pl-0 pr-3"
        color="black--text grey lighten-2"
        @click="back"
      >
        <v-icon
          class="ma-0 pa-0"
          color="grey darken-3"
        >
          mdi-chevron-left
        </v-icon>
        {{ $t('main.back') }}
      </v-btn>
      <v-col cols="7">
        <span class="text-h5">
          {{ document.fileName }}</span>
      </v-col>

      <v-col class="d-flex flex-row justify-end">
        <v-btn
          id="signature_confirmation_sign_btn"
          class="mx-4 px-4"
          color="success"
          :disabled="!(formValid && !!userDrawedSignatureImage)"
          :loading="isUploading"
          @click="signDoc()"
        >
          <v-icon class="mr-1 ml-0">mdi-draw-pen</v-icon>
          {{ $t('main.sign') }}
        </v-btn>
        <v-btn
          id="decline_btn"
          color="error"
          outlined
          class="px-2"
          :loading="isUploading"
          @click="showDeclineModal($event)"
        >
          <v-icon
            class="mr-1 ml-0"
          >
            mdi-close-circle-outline
          </v-icon>
          {{ $t('dataTable.content.action.decline') }}
        </v-btn>
      </v-col>
    </v-row>
    <v-row
      align="stretch"
      align-content="space-between"
    >
      <v-col md="6" sm="12" align="start">
        <!-- SIGNATURE + CRED  CONFIG-->
        <v-card v-show="pdfRendered">
          <v-container class="d-flex flex-column py-6 px-10">
            <v-form ref="form" v-model="formValid" @submit="signDoc()">
              <v-row>
                <v-card-subtitle>
                  <v-icon>mdi-information-outline</v-icon>
                  {{ $t('documentSignatureCustomization.content.signature.clerk.disclaimer') }}
                </v-card-subtitle>
              </v-row>
              <v-row>
                <v-text-field
                  id="subsigner_name"
                  v-model="subsignerName"
                  :label="$t('documentSignatureCustomization.content.signature.clerk.clientNameInput')"
                  :rules="[rules.required]"
                  hide-details="auto"
                ></v-text-field>
              </v-row>

              <v-row align-content="end" class="mt-6">
                <v-btn
                  id="draw_signature"
                  class="body-2 white--text modalButton"
                  @click="useCustomSignatureClick"
                >
                  <v-icon>mdi-pen</v-icon>
                  {{ $t('documentSignatureCustomization.content.signature.drawYourSignature') }}
                </v-btn>

                <v-btn
                  v-if="useCustomDrawnSignatureImage "
                  icon
                  @click="clearCutomSignatureImageClick"
                >
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </v-row>
            </v-form>
          </v-container>
        </v-card>
      </v-col>
      <!-- visibleSignatureComponent -->
      <v-col
        cols="6"
        md="6"
        sm="12"
        align="center"
        style="position: relative;"
      >
        <v-overlay
          :absolute="true"
          :value="isUploading"
        >
          <v-progress-circular
            indeterminate
            size="64"
            color="primary"
          ></v-progress-circular>
        </v-overlay>
        <visibleSignature
          ref="visibleSignature"
          :doc-data="documentBase64"
          :sign-prof="selectedSignatureProfile"
          :pdf-encrypted.sync="pdfEncrypted"
          :allow-sign-profile-editing="!predefinedSignatureParams"
          @updatedTextSize="updatedTextSize"
          @updatedSignatureProfileImage="updatedSignatureProfileImage"
          @pdfLoaded="pdfLoaded"
          @passwordSpecified="onPDFPasswordSpecified"
          @passwordCancelled="handlePasswordCancelled"
        />
      </v-col>
    </v-row>

    <v-row>
      <decline-reason-modal
        :visible="declineReasonVisible"
        :signature-id="+$route.params.signatureId"
        :current-signature-id="$route.params.currentSignatureId"
        @declined="back"
        @closed="declineReasonVisible = false"
      ></decline-reason-modal>
    </v-row>
    <v-row>
      <signature-drawer ref="signatureDrawer" v-model="showSignatureModal" @saved="handleCustomSignature"></signature-drawer>
    </v-row>
  </v-container>
</template>

<script>

import SignatureDrawer from '@/components/SignatureDrawer.vue'
import seal from '@/services/api/seal.js'
import signatures from '@/services/api/signatures'
import VisibleSignature from '../../components/VisibleSignature'
import { showConfirmationMenu, showRequestLoadingDialog, showToastError, showToastSuccess } from '../../event-bus'
import documents from '../../services/api/documents'
import users from '../../services/api/users'
import DeclineReasonModal from './DeclineReasonModal'

export default {
  name: 'ClerkModeSignatureCustomization',
  components: {
    SignatureDrawer,
    VisibleSignature,

    DeclineReasonModal
  },

  data() {
    return {
      formValid: true,
      documentBase64: null,
      document: {},
      pdfRendered: false,

      useCustomDrawnSignatureImage: false,
      userDrawedSignatureImage: null,
      sealStatus: {
        enabled: false,
        secureMode: false,
        activated: false
      },
      pdfEncrypted: false,
      pdf_password: null,
      signatureImg: null,
      signatureTextSize: 14,
      selectedSignatureProfile: {},
      draftRecipients: [{ userId: null }],

      // centralKeyId: 'central_key_id',
      centralKeyId: -1,

      isUploading: true,
      // decline reasoning:
      declineReasonVisible: false,
      showSignatureModal: false,
      subsignerName: '',
      rules: {
        required: (v) => !!v || !isNaN(parseFloat(v)) || this.$t('validation.form.requiredField')

      }

    }
  },

  computed: {
    currentUser() {
      return this.$store.state.currentUser
    },
    predefinedSignatureParams() {
      const { originX, originY, width, height, page } = this.$route.query
      if ([originX, originY, width, height, page].every(v => v === undefined)) {
        return null
      }
      const parsed = {
        originX: parseFloat(originX),
        originY: parseFloat(originY),
        width: parseFloat(width),
        height: parseFloat(height),
        page: parseInt(page)
      }

      const isValid =
      !isNaN(parsed.originX) &&
      !isNaN(parsed.originY) &&
      !isNaN(parsed.width) &&
      !isNaN(parsed.height) &&
      !isNaN(parsed.page)

      return isValid ? parsed : null
    },
    // from validating not working correctly on this page, so made this not so nice hack. todo: fix the validation issues

    sealActivated() {
      // return false
      return this.sealStatus.enabled && this.sealStatus.activated
    },

    sealEnabled() {
      // return false
      return this.sealStatus.enabled
    }

  },

  async mounted() {
    await this.downloadDocument()

    this.sealStatus = await seal.getSealStatus()

    const chain = this.$store.state.currentChain
    const id = +this.$route.params.signatureId || this.$route.params.currentSignatureId
    const signature = chain.signatures.find(x => x.id === id)
    this.subsignerName = signature.subsignerName || ''
    this.user = await users.getMyUser()
    // this.user.verified = true // todo remove in production
  },
  destroyed() {
    this.$store.commit('removeCurrentChain')
  },
  methods: {

    async downloadDocument() {
      this.isUploading = true
      try {
        const blob = await documents.downloadDocument(this.$route.params.docId)
        const docuDto = await documents.getDocument(this.$route.params.docId)
        this.document = docuDto
        this.pdfEncrypted = (docuDto.security === 'ENCRYPTED_WITH_OWNER_PASSWORD') || (docuDto.security === 'ENCRYPTED_WITH_USER_PASSWORD')
        const base64 = await this.blobToBase64(blob)
        this.documentBase64 = base64.split(',')[1]
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    handlePasswordCancelled() {
      showToastError(this.$t('toastMessage.cantDisplayPDFWithoutPasswordSpecified'))
      this.$router.push({ name: 'SignatureChains' })
    },

    onPDFPasswordSpecified(password) {
      this.pdf_password = password
    },

    blobToBase64(blob) {
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result)
        reader.readAsDataURL(blob)
      })
    },

    updatedTextSize(variable) {
      this.signatureTextSize = variable
    },

    async updatedSignatureProfileImage(variable) {
      this.signatureImg = variable
    },

    pdfLoaded(variable) {
      this.pdfRendered = true
      this.isUploading = false
    },

    async showDeclineModal(e) {
      const opts = {
        title: this.$t('dialog.confirmation.title'),
        posX: e.clientX,
        posY: e.clientY
      }
      const conf = await showConfirmationMenu(opts)
      if (!conf) {
        return
      }
      this.declineReasonVisible = true
    },

    async signDoc() {
      if (!this.$refs.form.validate()) {
        return
      }

      const isSealSignMode = this.isSealSignMode()

      this.isUploading = isSealSignMode

      const signRequest = {
        useSeal: isSealSignMode,
        userImageDataUrl: this.userDrawedSignatureImage,

        signatures: []
      }

      this.getSignatureFieldParameters()

      if (this.pdfEncrypted && this.pdf_password) {
        const documentsOptions = []
        const documentsOption = {
          docId: this.document.id,
          passwordProtection: this.pdf_password
        }
        documentsOptions.push(documentsOption)
        this.$set(signRequest, 'documentsOptions', documentsOptions)
      }

      const signature = {
        signatureId: !this.$route.params.signatureId != null ? this.$route.params.signatureId : null,
        currentSignatureId: !this.$route.params.currentSignatureId != null ? this.$route.params.currentSignatureId : null,
        subsignerName: this.subsignerName
      }
      const visibleSignature = this.$refs.visibleSignature
      const usedSignatureFieldParameters = visibleSignature.getSignatureFieldParameters()

      if (usedSignatureFieldParameters) {
        this.$set(signature, 'signatureFieldParameters', { ...usedSignatureFieldParameters })
      }
      signRequest.signatures.push(signature)

      try {
        await signatures.sign(signRequest, null)
        this.back()
        showToastSuccess(this.$t('toastMessage.succesfullySigned'))
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    isSealSignMode() {
      return this.sealActivated
    },

    back() {
      this.declineReasonVisible = false
      this.$router.push({ name: 'SignatureChains' })
    },

    getSignatureFieldParameters() {
      const visibleSignatureComponent = this.$refs.visibleSignature

      if (visibleSignatureComponent) {
        const signatureImageFieldParameters = visibleSignatureComponent.getSignatureFieldParameters()
        if (signatureImageFieldParameters) {
          this.selectedSignatureProfile.savedSignatureFieldParameters = Object.assign({}, this.selectedSignatureProfile.savedSignatureFieldParameters, signatureImageFieldParameters)
        }
      } else {
        console.error('visibleSignature component not found.')
      }
    },

    openSignatureModal() {
      this.$refs.signatureDrawer.dialog = true
    },
    async handleCustomSignature(imageData) {
      if (!this.selectedSignatureProfile) {
        this.selectedSignatureProfile = {}
      }
      this.userDrawedSignatureImage = imageData

      const req = {
        userImageDataUrl: imageData,
        subsignerName: this.subsignerName
      }
      const mergedBase64 = await signatures.combineImage(req)
      this.selectedSignatureProfile.sourceImageDataUrl = mergedBase64.imageDataUrl
      this.selectedSignatureProfile.savedSignatureFieldParameters = this.predefinedSignatureParams
      this.$nextTick(() => {
        const visibleSignatureComponent = this.$refs.visibleSignature
        visibleSignatureComponent.updateSignatureProfile(this.selectedSignatureProfile)
      })
    },
    setSignatureImage(file, imageDataUri, callPreview = true) {
      let updatedDataUri = ''
      const splittedSigImgDataUrl = imageDataUri.split(';')
      updatedDataUri = updatedDataUri.concat(splittedSigImgDataUrl[0], ';filename=' + file.name + ';', splittedSigImgDataUrl[1])
      this.$set(this.signatureProfile, 'sourceImageDataUrl', updatedDataUri)
      if (callPreview) {
        this.initPreview()
      }
    },
    useCustomSignatureClick() {
      this.useSignatureProfile = false
      this.useCustomDrawnSignatureImage = true
      this.openSignatureModal()
    },

    clearCutomSignatureImageClick() {
      this.useCustomDrawnSignatureImage = false
      this.userDrawedSignatureImage = null
      if (this.selectedSignatureProfile) {
        this.selectedSignatureProfile.sourceImageDataUrl = undefined
        this.$nextTick(() => {
          const visibleSignatureComponent = this.$refs.visibleSignature
          visibleSignatureComponent.updateSignatureProfile(this.selectedSignatureProfile)
        })
      }
      this.useSignatureProfile = false
    },
    async loadImage(base64Src) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.src = base64Src
        img.onload = () => resolve(img)
        img.onerror = (err) => reject(err)
      })
    }

  }
}
</script>

<style>
.BlueWarning {
  color: var(--v-accent-base);
}
</style>
